/* -------------------------- Design imports start -------------------------- */
import { Grid } from '@mui/material'

/* -------------------------- Design imports end -------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";
import { useTranslation } from 'react-i18next'

import GetConstant from './utils/constants'
import MultilevelDrawer from '../../components/layout/MultilevelDrawer'
import { missingRequiredFormFields } from '../../utils/functions'
import { Form, type FormFieldType, type FormObject } from "../Form";
import { type APICategory } from "../../generated-types";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: APICategory | undefined
    setState: React.Dispatch<React.SetStateAction<APICategory | undefined>>
  }
  onClose?: () => void
  onCancel?: () => void
  onCreateConfirm: (input: any) => void
  enableEditMode?: boolean
  onEditConfirm?: (input: any) => void
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function CategoryDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    open,
    setOpen,
    onClose = () => null,
    formState,
    onCreateConfirm = () => null,
    enableEditMode = false,
    onEditConfirm = () => alert('TODO: provide a onEditConfirm callback!'),
  } = props
  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (preventOnClose = false) => {
    // reset modal to initial state
    formState.setState(undefined)
    setOpen(false)

    !preventOnClose && onClose()
  }

  const handleCreateConfirm = (): void => {
    if (!formState.state) {
      throw new Error('formState is undefined')
    }
    // posting request to server and waiting on response
    onCreateConfirm({ ...formState.state })
    handleClose(true)
  }

  const handleEditConfirm = () => {
    // posting request to server and waiting on response
    onEditConfirm(formState.state)
    handleClose(true)
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const formFieldsCategory: FormFieldType[] = GetConstant({
    name: 'categoryFormFields',
  }) as FormFieldType[]

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */

  if (!formState.state) {
    return <></>
  }

  return (
    <MultilevelDrawer
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      size="small"
      title={t('item:content.heading.category') }
      confirmButtonProps={{
        text: enableEditMode
          ? t('common:interaction.button.save')
          : t('common:interaction.button.create'),
        onConfirm: () => {
          enableEditMode
            ? handleEditConfirm()
            : handleCreateConfirm();
        },
        disabled: missingRequiredFormFields(formFieldsCategory, formState.state),
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Form
            editing={true}
            formFields={formFieldsCategory}
            formObject={formState.state}
            onChange={(value: FormObject) => formState.setState(value as APICategory)}
          />
        </Grid>
      </Grid>
    </MultilevelDrawer>
  )
}
